import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, FileText, Handshake, LineChart, Sparkles, LogOut, Plus, FolderOpen, ChevronRight, Zap, Menu, X, Store } from "lucide-react";
import PaywallModal from "@/components/PaywallModal";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/editor", label: "Script Editor", icon: FileText, testid: "nav-editor" },
  { to: "/sponsors", label: "Sponsors", icon: Handshake, testid: "nav-sponsors" },
  { to: "/marketplace", label: "Marketplace", icon: Store, testid: "nav-marketplace" },
  { to: "/analytics", label: "Analytics", icon: LineChart, testid: "nav-analytics" },
  { to: "/repurpose", label: "Repurpose", icon: Sparkles, testid: "nav-repurpose" },
];

export default function AppLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [paywall, setPaywall] = useState({ open: false, message: "" });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
      api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
      api.get("/billing/usage").then(({ data }) => setUsage(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const onPaywall = (e) => setPaywall({ open: true, message: e.detail?.message || "This feature requires a paid plan." });
    window.addEventListener("sd:paywall", onPaywall);
    return () => window.removeEventListener("sd:paywall", onPaywall);
  }, []);

  // Close mobile sidebar when route changes
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const createProject = async () => {
    const title = prompt("Project title?");
    if (!title) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { title, kind: "video" });
      setProjects((p) => [data, ...p]);
      toast.success("Project created");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Could not create project");
    } finally { setCreating(false); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-zinc-500">
        <div className="sd-label">Loading workspace…</div>
      </div>
    );
  }

  const sidebar = (
    <>
      <div className="px-6 h-16 flex items-center gap-2 border-b border-[#27272a] justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
          <span className="font-display text-xl tracking-tight">SourceDeck</span>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden text-zinc-500 hover:text-white"
          data-testid="sidebar-close-btn"
          aria-label="Close menu"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      <div className="px-4 py-6 flex-1 overflow-y-auto">
        <div className="sd-label px-2 mb-3">WORKSPACE</div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-9 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-[#141414] text-white border-l-2 border-[#FF3B30]" : "text-zinc-400 hover:text-white hover:bg-[#141414]"
                }`
              }
            >
              <n.icon size={15} strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-10 flex items-center justify-between px-2">
          <div className="sd-label">PROJECTS</div>
          <button
            onClick={createProject}
            disabled={creating}
            className="text-zinc-500 hover:text-white transition-colors"
            data-testid="sidebar-new-project-btn"
            title="New project"
          >
            <Plus size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="mt-3 space-y-0.5">
          {projects.length === 0 && (
            <div className="text-xs text-zinc-600 px-3">No projects yet.</div>
          )}
          {projects.map((p) => (
            <div
              key={p.project_id}
              className="flex items-center gap-2 px-3 h-8 text-xs text-zinc-400 hover:text-white hover:bg-[#141414] cursor-default transition-colors"
              data-testid={`sidebar-project-${p.project_id}`}
            >
              <FolderOpen size={12} strokeWidth={1.5} />
              <span className="truncate">{p.title}</span>
              <ChevronRight size={12} strokeWidth={1.5} className="ml-auto opacity-40" />
            </div>
          ))}
        </div>
      </div>

      {/* Plan badge / Upgrade CTA */}
      {sub && (
        sub.active ? (
          <Link
            to="/pricing"
            className="mx-4 mb-3 border border-[#27272a] p-3 flex items-center gap-2 hover:border-zinc-500 transition-colors"
            data-testid="sidebar-plan-badge"
          >
            <span className="w-1.5 h-1.5 bg-[#34C759]" />
            <div className="min-w-0 flex-1">
              <div className="sd-label">PLAN · {(sub.plan_name || sub.tier).toUpperCase()}</div>
              <div className="text-[10px] text-zinc-500 font-mono truncate mt-0.5">
                {usage?.limits?.ai_actions == null
                  ? "Unlimited AI · unlimited projects"
                  : `${usage.usage.ai_actions}/${usage.limits.ai_actions} AI · ${usage.usage.projects}/${usage.limits.projects} projects`}
              </div>
            </div>
          </Link>
        ) : (
          <Link
            to="/pricing"
            className="mx-4 mb-3 p-3 flex items-center gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] hover:bg-[#FF3B30]/[0.12] transition-colors"
            data-testid="sidebar-upgrade-link"
          >
            <Zap size={14} strokeWidth={1.5} className="text-[#FF3B30]" />
            <div className="min-w-0 flex-1">
              <div className="sd-label text-[#FF3B30]">FREE PLAN</div>
              <div className="text-[10px] text-zinc-400 mt-0.5">
                {usage?.limits?.ai_actions != null
                  ? `${usage.usage.ai_actions}/${usage.limits.ai_actions} AI · ${usage.usage.projects}/${usage.limits.projects} projects · upgrade →`
                  : "Upgrade to unlock all AI →"}
              </div>
            </div>
          </Link>
        )
      )}

      {/* User */}
      <div className="border-t border-[#27272a] p-4 flex items-center gap-3">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-[#27272a]" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-8 h-8 bg-[#141414] border border-[#27272a] flex items-center justify-center font-mono text-xs">
            {user.name?.[0] || "?"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">{user.name}</div>
          <div className="text-[10px] text-zinc-500 font-mono truncate">{user.email}</div>
        </div>
        <button
          onClick={logout}
          className="text-zinc-500 hover:text-white transition-colors"
          data-testid="sidebar-logout-btn"
          title="Log out"
        >
          <LogOut size={15} strokeWidth={1.5} />
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain md:flex">
      {/* Mobile top bar (< md only) */}
      <div className="md:hidden sticky top-0 z-40 h-14 px-4 flex items-center gap-3 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-[#27272a]">
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-white p-1 -ml-1"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu size={20} strokeWidth={1.5} />
        </button>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
          <span className="font-display text-lg tracking-tight">SourceDeck</span>
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/60"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — fixed on mobile (drawer), sticky on desktop */}
      <aside
        className={`
          md:w-[260px] md:shrink-0 md:border-r md:border-[#27272a] md:bg-[#0a0a0a] md:sticky md:top-0 md:h-screen md:translate-x-0 md:flex md:flex-col
          fixed left-0 top-0 bottom-0 z-50 w-[280px] bg-[#0a0a0a] border-r border-[#27272a] flex flex-col
          transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:-translate-x-0"}
        `}
        data-testid="app-sidebar"
      >
        {sidebar}
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      <PaywallModal
        open={paywall.open}
        onClose={() => setPaywall({ open: false, message: "" })}
        message={paywall.message}
      />
    </div>
  );
}
